/**
 * The owning scope of a record, as a field gate carries it.
 *
 * Patient data is decided per record: the team that owns it reads it, and
 * anyone else needs an explicit grant at a scope that contains it. A field gate
 * therefore names the record's organisation, workspace and team; one without
 * them is denied.
 */
import { isRecord } from './guards';
import type { Gate, RecordScope } from './types';
import { InstructionError } from './values';

const EXPECTED_SCOPE = '{ organizationId, workspaceId?, teamId? } of non-empty strings';

/** A record scope while it is parsed: every id still optional. */
interface RecordScopeDraft {
  organizationId?: string;
  workspaceId?: string;
  teamId?: string;
}

/** Parses a record scope at the authoring boundary: known keys only, non-empty string ids, the organisation required. */
export function parseRecordScope<V>(value: V, where: string): RecordScope {
  if (!isRecord(value)) throw new InstructionError(`${where} expects ${EXPECTED_SCOPE}`);
  const scope: RecordScopeDraft = {};
  for (const [key, entry] of Object.entries(value)) {
    if (entry === undefined) continue;
    if (!isNonEmptyString(entry)) throw new InstructionError(`${where}: ${key} must be a non-empty string`);
    switch (key) {
      case 'organizationId':
      case 'workspaceId':
      case 'teamId':
        scope[key] = entry;
        break;
      default:
        throw new InstructionError(`${where}: unknown key "${key}"; expected ${EXPECTED_SCOPE}`);
    }
  }
  const { organizationId } = scope;
  if (organizationId === undefined) throw new InstructionError(`${where} needs an organizationId`);
  return { ...scope, organizationId };
}

/** Whether a gate's record scope is usable: an organisation is the least a record belongs to. */
export function hasRecordScope(record: RecordScope | undefined): record is RecordScope {
  return record !== undefined && isNonEmptyString(record.organizationId);
}

/** Attaches a parsed record scope to a field gate; a record on a gate that names no field is an authoring error. */
export function withRecord<V>(gate: Gate | undefined, record: V, where: string): Gate {
  if (gate?.field === undefined) {
    throw new InstructionError(`${where} applies to a field gate such as "read person.nhsNumber"`);
  }
  return { ...gate, record: parseRecordScope(record, where) };
}

function isNonEmptyString<T>(value: T): value is T & string {
  return typeof value === 'string' && value.length > 0;
}
